/**
 * RequirePharmacySession.tsx — the presentation-only gate on /pharmacy (R41).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY it mirrors `auth/RequireRole` rather than reusing it: RequireRole reads
 * the patient/staff AuthContext and bounces to `/auth?next=…`. A pharmacist has
 * no account there, and `/auth` offers them nothing they can complete — sending
 * them to a password form they can never satisfy would read as a broken product.
 * The pharmacy lane's own front door is `/auth-pharmacy`.
 *
 * NO `?next=`: the way back in is an emailed single-use link, so a remembered
 * destination cannot be honoured and would only be a redirect target to police.
 * The queue is the console's landing screen anyway.
 *
 * This is UI convenience ONLY. The gateway authorises every call server-side
 * (`@Roles('PHARMACY')`, organisation scope taken from the verified session), so
 * a hand-edited URL yields a console shell with 401s in it, never data.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { usePharmacySession } from './usePharmacySession';

export function RequirePharmacySession({ children }: { children: ReactNode }) {
  const { session } = usePharmacySession();
  // The store is synchronous (localStorage), so there is no hydrating state to
  // hold — either a live session is on the machine or it is not.
  if (!session) return <Navigate to="/auth-pharmacy" replace />;
  return <>{children}</>;
}
